import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Box, Paper, Chip, Button, Typography, Tooltip, CircularProgress, Alert } from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import { Link as RouterLink } from 'react-router-dom';
import axios from 'axios';

const STATUS_COLOR = { ok: 'success', warn: 'warning', stale: 'error' };
const POLL_MS = 5000;

/**
 * Bank feed freshness strip + one-click "Refresh & Book".
 * POST /api/accounting/bank-feeds/refresh syncs the API bank connectors
 * (wise/revolut/paypal) and then runs the bank->GL booking chain server-side;
 * this component just starts the run and polls status. Amex has no API, so
 * its chip links to the manual statement upload instead.
 */
const BankFeedsRefresh = () => {
  const [freshness, setFreshness] = useState([]);
  const [runState, setRunState] = useState(null);
  const [starting, setStarting] = useState(false);
  const [error, setError] = useState(null);
  const pollRef = useRef(null);

  const fetchFreshness = useCallback(async () => {
    try {
      const r = await axios.get('/api/accounting/bank-feeds/freshness');
      setFreshness(r.data.sources || []);
    } catch (e) { /* freshness strip is best-effort; button still works */ }
  }, []);

  const fetchStatus = useCallback(async () => {
    try {
      const r = await axios.get('/api/accounting/bank-feeds/refresh/status');
      setRunState(r.data.state);
      return r.data.state;
    } catch (e) { return null; }
  }, []);

  useEffect(() => {
    fetchFreshness();
    fetchStatus();
  }, [fetchFreshness, fetchStatus]);

  // Poll while a run is active; refresh the strip when it finishes.
  useEffect(() => {
    if (runState?.active && !pollRef.current) {
      pollRef.current = setInterval(async () => {
        const s = await fetchStatus();
        if (!s?.active) {
          clearInterval(pollRef.current);
          pollRef.current = null;
          fetchFreshness();
        }
      }, POLL_MS);
    }
    return () => { if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; } };
  }, [runState?.active, fetchStatus, fetchFreshness]);

  const handleRefresh = async () => {
    setStarting(true);
    setError(null);
    try {
      await axios.post('/api/accounting/bank-feeds/refresh');
      await fetchStatus();
    } catch (e) {
      if (e.response?.status === 409) {
        setRunState(e.response.data.state);
        setError('A booking run is already in progress');
      } else if (e.response?.status === 403) {
        setError(e.response.data?.message || 'Not permitted');
      } else {
        setError(e.response?.data?.message || 'Failed to start refresh');
      }
    } finally {
      setStarting(false);
    }
  };

  const active = Boolean(runState?.active);
  const phaseLabel = () => {
    if (!runState) return null;
    if (runState.phase === 'sync' && runState.sync) {
      const done = runState.sync.jobs?.filter(j => ['completed', 'failed', 'cancelled', 'timeout', 'enqueue_failed'].includes(j.status)).length || 0;
      return `Syncing bank connectors (${done}/${runState.sync.total})`;
    }
    if (runState.phase === 'book' && runState.chain) {
      const steps = runState.chain.steps || [];
      const total = steps.length || 7;
      const doneSteps = steps.filter(s => ['completed', 'failed', 'cancelled'].includes(s.status)).length;
      const current = steps.find(s => ['queued', 'running'].includes(s.status));
      return `Booking to GL: step ${Math.min(doneSteps + 1, total)} of ${total}${current ? ` (${current.name.replace(/_/g, ' ')})` : ''}`;
    }
    if (runState.phase === 'done') return `Last refresh completed ${new Date(runState.finishedAt).toLocaleTimeString()}`;
    if (runState.phase === 'done_partial') {
      const failed = runState.sync?.failed || 0;
      return `Last refresh finished with issues${failed ? ` (${failed} connector sync${failed > 1 ? 's' : ''} failed)` : ''} — some rows book on the next scheduled cycle`;
    }
    if (runState.phase === 'aborted_busy') return 'Refresh yielded to a scheduled run; the next cycle covers remaining rows';
    if (runState.phase === 'error') return `Last refresh error: ${runState.error || 'unknown'}`;
    return null;
  };

  return (
    <Paper variant="outlined" sx={{ p: 2, mb: 4 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
        <AccountBalanceIcon color="action" sx={{ mr: 0.5 }} />
        <Typography variant="subtitle1" fontWeight={600} sx={{ mr: 1 }}>Bank Feeds</Typography>
        {freshness.map(f => (
          <Tooltip
            key={f.source}
            title={f.manual
              ? `Manual statement upload only. Last transaction: ${f.last_txn_date || 'never'}. Click to upload a statement.`
              : `Last transaction: ${f.last_txn_date || 'never'} (threshold ${f.threshold_days}d)`}
          >
            <Chip
              size="small"
              label={`${f.source}: ${f.age_days === null ? 'no data' : `${f.age_days}d`}`}
              aria-label={f.manual
                ? `${f.source}: manual statement upload, last transaction ${f.last_txn_date || 'never'}`
                : `${f.source}: last transaction ${f.last_txn_date || 'never'}, ${f.age_days === null ? 'no data' : `${f.age_days} days ago`} (threshold ${f.threshold_days} days)`}
              color={STATUS_COLOR[f.status] || 'default'}
              variant={f.status === 'ok' ? 'outlined' : 'filled'}
              icon={f.manual ? <UploadFileIcon /> : undefined}
              component={f.manual ? RouterLink : 'div'}
              to={f.manual ? '/connectors' : undefined}
              clickable={f.manual}
            />
          </Tooltip>
        ))}
        <Button
          variant="contained"
          size="small"
          sx={{ ml: 'auto' }}
          startIcon={active || starting ? <CircularProgress size={16} color="inherit" /> : <RefreshIcon />}
          disabled={active || starting}
          onClick={handleRefresh}
        >
          {active ? 'Refreshing…' : 'Refresh & Book'}
        </Button>
      </Box>
      {(phaseLabel() || error) && (
        <Box sx={{ mt: 1 }}>
          {error && <Alert severity="warning" sx={{ py: 0 }}>{error}</Alert>}
          {phaseLabel() && !error && (
            <Typography variant="body2" color="text.secondary">{phaseLabel()}</Typography>
          )}
        </Box>
      )}
    </Paper>
  );
};

export default BankFeedsRefresh;
